<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, ContentType, Tag } from '@/models/content'
import BaseLink from '@/components/common/BaseLink.vue'

const props = defineProps({
  contentType: {
    type: String as PropType<ContentType>,
    default: 'article',
    validator(value: string) {
      return ['article', 'chart', 'video', 'magazine', 'presentation'].includes(
        value,
      )
    },
  },
  slug: {
    type: [String, Number],
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  pillsLimit: {
    type: Number,
    default: 2,
  },
  coverUrl: {
    type: String,
    required: true,
  },
})

const { $cloudinary } = useNuxtApp()

const formattedDuration = useFormattedDuration(
  props.contentType,
  props.duration,
)

const isVideo = computed(() => props.contentType === 'video')
const href = useContentHref(props.contentType, props.slug)
const coverPosition = isVideo.value
  ? 'object-left'
  : useCoverPosition(props.coverUrl)

const publishedDateAndDuration = computed(() => {
  const formattedDurationString = !isVideo.value
    ? `· ${formattedDuration.value}`
    : ''
  return `${props.publishedDate} ${formattedDurationString}`
})

const limitedPills = computed(() => props.pills.slice(0, props.pillsLimit))
const coverUrlResized = isVideo.value
  ? $cloudinary.resize(props.coverUrl, 'vertical-video-card-thumbnail')
  : $cloudinary.resize(props.coverUrl, 'archive-card-thumbnail')
</script>

<template>
  <BaseLink :href="href" as="a" variant="unstyled">
    <ArchiveCard
      :date-and-duration="publishedDateAndDuration"
      :title="title"
      :pills="limitedPills"
    >
      <template #cover>
        <img
          :src="coverUrlResized"
          class="h-full min-w-full object-cover"
          :class="coverPosition"
          alt=""
          width="100"
          height="100"
        />
        <div class="absolute top-0 h-full w-full border-2 border-black/10" />

        <div class="absolute right-1 top-1 hidden sm:right-3 sm:top-3 sm:flex">
          <ContentTypeBadge :type="contentType" />
        </div>

        <!-- Duration badge -->
        <div v-if="isVideo" class="absolute bottom-0 right-0 p-3">
          <div
            class="rounded-3xl bg-black px-2.5 py-1.5 text-xxs font-semibold text-white"
          >
            {{ formattedDuration }}
          </div>
        </div>
      </template>

      <template #icon-mobile>
        <ContentTypeBadge :type="contentType" icon-gray />
      </template>

      <template #details>
        <div class="custom-line-clamp" data-testid="excerpt" v-html="excerpt" />

        <time
          class="text-sm font-semibold text-s-700"
          data-testid="date-on-desktop"
        >
          {{ publishedDateAndDuration }}
        </time>
      </template>
    </ArchiveCard>
  </BaseLink>
</template>
